import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import TagSelector from './TagSelector';
import RolledOptions from './RolledOptions';
import './OpenPositions.css';

function OpenPositions({ username, onLogout, activeSubmenu, setActiveSubmenu }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  // Default showSynthetic to false for rolled-options, true for other submenus
  const [showSynthetic, setShowSynthetic] = useState(activeSubmenu !== 'rolled-options');
  const [expandedSynthetics, setExpandedSynthetics] = useState(new Set());
  const [accounts, setAccounts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [hoveredCell, setHoveredCell] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const [rolledOptionsData, setRolledOptionsData] = useState({}); // Map of underlying_symbol+account -> total_pnl
  const [vspData, setVspData] = useState({}); // Map of position_id -> { totalPnL, vsp, diff }

  useEffect(() => {
    fetchAccounts();
    fetchRolledOptions();
  }, []);

  useEffect(() => {
    if (positions.length > 0) {
      fetchVspData();
    }
  }, [positions, selectedAccount]);

  const fetchRolledOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/trades/rolled_options/`);
      const groups = response.data.groups || [];
      // Create a map: key = `${underlying_symbol}_${account_ibkr_id}`, value = total_pnl
      // Use account_ibkr_id to match with OpenPositions which use IBKR account ID
      const rolledMap = {};
      groups.forEach(group => {
        // Use account_ibkr_id if available, fallback to account_id for backward compatibility
        const accountId = group.account_ibkr_id || group.account_id;
        if (accountId && group.underlying_symbol) {
          const key = `${group.underlying_symbol}_${accountId}`;
          rolledMap[key] = parseFloat(group.total_pnl || 0);
        }
      });
      setRolledOptionsData(rolledMap);
    } catch (err) {
      console.error('Error fetching rolled options:', err);
      setRolledOptionsData({});
    }
  };

  // Parse date from IBKR format (DD/MM/YY or ISO)
  const parseIBKRDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Try DD/MM/YY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const fullYear = year < 50 ? 2000 + year : 1900 + year;
          return new Date(fullYear, month, day);
        }
      }
      // Try ISO format
      return new Date(dateStr);
    } catch (e) {
      return null;
    }
  };

  const fetchVspData = async () => {
    try {
      // Calculate VSP for ALL stock positions (not just those with tag)
      const allStocks = positions.filter(position => position.asset_category === 'STK');
      
      if (allStocks.length === 0) {
        setVspData({});
        return;
      }

      // Get account filter
      let accountIdParam = null;
      if (selectedAccount && selectedAccount !== 'all' && accounts.length > 0) {
        const account = accounts.find(acc => 
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          accountIdParam = account.broker_account_id;
        }
      }

      // Fetch all trades (we'll filter in frontend)
      let tradesUrl = `${API_URL}/portfolio/trades/?page_size=all`;
      if (accountIdParam) {
        tradesUrl += `&account_id=${accountIdParam}`;
      }
      
      const tradesResponse = await axios.get(tradesUrl);
      const allTrades = Array.isArray(tradesResponse.data.results || tradesResponse.data) 
        ? (tradesResponse.data.results || tradesResponse.data) 
        : [];

      // Calculate VSP data for each stock
      const vspMap = {};
      
      allStocks.forEach(stock => {
        const stockSymbol = stock.underlying_symbol || stock.symbol;
        if (!stockSymbol) return;

        // Filter trades:
        // 1. Same symbol (underlying_symbol or symbol matches)
        // 2. Has "virtual stock price" tag
        // 3. Closed option trades (open_close_indicator = 'C' or 'CLOSE')
        const relevantTrades = allTrades.filter(trade => {
          // Check symbol match
          const tradeSymbol = trade.underlying_symbol || (trade.symbol ? trade.symbol.split()[0] : null);
          if (!tradeSymbol || tradeSymbol.toUpperCase() !== stockSymbol.toUpperCase()) {
            return false;
          }

          // Check tag - must have "virtual stock price" tag
          const tradeTags = trade.tags || [];
          const hasVspTag = tradeTags.some(tag => tag.name && tag.name.toLowerCase() === 'virtual stock price');
          if (!hasVspTag) return false;

          // Check if closed option trade
          const indicator = (trade.open_close_indicator || '').toUpperCase();
          const isClosed = indicator === 'C' || indicator === 'CLOSE';
          if (!isClosed) return false;

          // Must be an option trade (has put_call field)
          if (!trade.put_call) return false;

          return true;
        });

        // Sum realized P&L from all closed option trades with VSP tag
        const totalPnL = relevantTrades.reduce((sum, trade) => {
          return sum + (parseFloat(trade.fifo_pnl_realized) || 0);
        }, 0);

        // Only calculate VSP if we have trades and an open price
        const openPrice = parseFloat(stock.open_price) || 0;
        if (totalPnL !== 0 && openPrice > 0) {
          // VSP = open_price - (totalPnL / 100)
          // Division by 100 because options are in contracts (100 shares) and stock price is per share
          const vsp = openPrice - (totalPnL / 100);
          const diff = openPrice - vsp; // This equals totalPnL / 100

          vspMap[stock.id] = {
            totalPnL,
            vsp,
            diff
          };
        }
      });

      setVspData(vspMap);
    } catch (err) {
      console.error('Error fetching VSP data:', err);
      setVspData({});
    }
  };

  // Update showSynthetic when submenu changes
  useEffect(() => {
    const currentSubmenu = activeSubmenu || 'options'; // Default to 'options'
    if (currentSubmenu === 'rolled-options' || currentSubmenu === 'stocks') {
      setShowSynthetic(false);
    } else if (currentSubmenu === 'options' || currentSubmenu === 'open-positions' || currentSubmenu === 'short-put-exposure') {
      setShowSynthetic(true);
    }
  }, [activeSubmenu]);

  useEffect(() => {
    fetchPositions();
  }, [selectedAccount, selectedBroker, selectedType, showSynthetic]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      setError('Error fetching accounts: ' + err.message);
      console.error(err);
    }
  };

  // Helper function to get account display name
  const getAccountDisplayName = (accountId) => {
    if (!accountId) return '-';
    const account = accounts.find(acc => 
      acc.broker_account_id === accountId || 
      acc.id === parseInt(accountId) ||
      acc.id?.toString() === accountId?.toString()
    );
    return account?.display_name || accountId;
  };

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/portfolio/open-positions/?page_size=all`;
      if (selectedBroker && selectedBroker !== 'all') {
        url += `&broker=${selectedBroker}`;
      }
      if (selectedAccount && selectedAccount !== 'all') {
        const account = accounts.find(acc =>
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          url += `&account_id=${account.broker_account_id}`;
        }
      }
      
      const response = await axios.get(url);
      const data = response.data.results || response.data;
      setPositions(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching open positions: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = 'EUR') => {
    if (!value || value === '0.00') {
      const symbol = getCurrencySymbol(currency);
      return `${symbol} 0.00`;
    }
    const num = parseFloat(value);
    const currencyCode = currency || 'EUR';
    // Format number with 2 decimal places
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol} ${formattedNumber}`;
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CHF': 'CHF ',
      'CAD': 'C$',
      'AUD': 'A$',
      'CNY': '¥',
      'HKD': 'HK$',
      'SGD': 'S$',
    };
    return symbols[currency?.toUpperCase()] || currency || '€';
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  const formatInteger = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Math.round(parseFloat(value));
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Format last updated timestamp for tooltip (Amsterdam timezone)
  const formatLastUpdated = (updatedAt) => {
    if (!updatedAt) return 'Not yet updated';
    
    try {
      const updated = new Date(updatedAt);
      
      // Check if date is valid
      if (isNaN(updated.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const updatedDate = new Date(updated.getFullYear(), updated.getMonth(), updated.getDate());
      
      // Format time in Amsterdam timezone
      const timeStr = updated.toLocaleTimeString('nl-NL', { 
        hour: '2-digit', 
        minute: '2-digit', 
        timeZone: 'Europe/Amsterdam' 
      });
      
      if (updatedDate.getTime() === today.getTime()) {
        return `Latest updated today at: ${timeStr}`;
      } else {
        const dateStr = updated.toLocaleDateString('nl-NL', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric', 
          timeZone: 'Europe/Amsterdam' 
        });
        return `Last updated: ${dateStr} ${timeStr}`;
      }
    } catch (e) {
      console.error('Error formatting last updated:', e, updatedAt);
      return 'Error formatting date';
    }
  };

  // Check if delta value was not updated (timestamp updated but value not)
  const isDeltaValueStale = (position) => {
    if (!position.delta_updated_at || !position.delta_value_updated_at) {
      return false;
    }
    const timestampUpdated = new Date(position.delta_updated_at);
    const valueUpdated = new Date(position.delta_value_updated_at);
    // If timestamp is more recent than value update, value is stale
    return timestampUpdated > valueUpdated;
  };

  // Handle cell hover for IV/Delta tooltip
  const handleCellMouseEnter = (position, field) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Set timeout for 2 seconds
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCell({ positionId: position.id, field });
    }, 2000);
  };

  const handleCellMouseLeave = () => {
    // Clear timeout if mouse leaves before 2 seconds
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredCell(null);
  };

  // Helper function to parse OCC symbol and build leg
  const parseOccSymbol = (occSymbol) => {
    if (!occSymbol) return null;
    
    try {
      // Remove all whitespace
      const cleanSymbol = occSymbol.replace(/\s+/g, '');
      
      // Basic length check
      if (cleanSymbol.length < 16) {
        return null;
      }

      const ticker = cleanSymbol.slice(0, -15);
      const date = cleanSymbol.slice(-15, -9);      // YYMMDD
      const optionType = cleanSymbol.slice(-9, -8); // C or P
      const strikeRaw = cleanSymbol.slice(-8);      // 8-digit strike

      // Validate ticker (US equities typically 1–6 chars)
      if (!/^[A-Z]{1,6}$/.test(ticker)) {
        return null;
      }

      // Validate option type
      if (optionType !== 'C' && optionType !== 'P') {
        return null;
      }

      // Validate expiration date format (YYMMDD)
      if (!/^\d{6}$/.test(date)) {
        return null;
      }

      // Validate strike
      if (!/^\d+$/.test(strikeRaw)) {
        return null;
      }

      const strike = parseInt(strikeRaw) / 1000;
      const strikeFormatted = Number.isInteger(strike) ? strike.toString() : strike.toString();

      return {
        ticker,
        date,
        optionType,
        strikeFormatted,
        leg: `.${ticker}${date}${optionType}${strikeFormatted}`
      };
    } catch (error) {
      return null;
    }
  };

  // Calculate OptionStrat URL from OCC symbol (similar to optionstrat.py logic)
  const calculateOptionStratUrl = (occSymbol, position, syntheticComponents = null) => {
    // Handle synthetic positions
    if (position.asset_category === 'Synthetic' && syntheticComponents) {
      try {
        const components = Array.isArray(syntheticComponents) ? syntheticComponents : [];
        if (components.length !== 2) {
          return null;
        }

        // Find call and put components
        const callComponent = components.find(c => (c.put_call || '').toUpperCase() === 'C');
        const putComponent = components.find(c => (c.put_call || '').toUpperCase() === 'P');
        
        if (!callComponent || !putComponent || !callComponent.symbol || !putComponent.symbol) {
          return null;
        }

        const callParsed = parseOccSymbol(callComponent.symbol);
        const putParsed = parseOccSymbol(putComponent.symbol);
        
        if (!callParsed || !putParsed) {
          return null;
        }

        // Check if same underlying, strike, and expiry
        if (callParsed.ticker !== putParsed.ticker || 
            callParsed.strikeFormatted !== putParsed.strikeFormatted ||
            callParsed.date !== putParsed.date) {
          return null;
        }

        const ticker = callParsed.ticker;
        const callPos = parseFloat(callComponent.position || 0);
        const putPos = parseFloat(putComponent.position || 0);
        
        // Determine if long or short synthetic
        // Long synthetic = long call + short put
        // Short synthetic = short call + long put
        const isLongSynthetic = callPos > 0 && putPos < 0;
        const isShortSynthetic = callPos < 0 && putPos > 0;
        
        if (!isLongSynthetic && !isShortSynthetic) {
          return null;
        }

        const strategy = isLongSynthetic ? 'long-synthetic-future' : 'short-synthetic-future';
        
        // Build legs: negative position gets "-" prefix, positive doesn't
        // Add open price to each leg if available
        let callLeg = callPos > 0 ? callParsed.leg : `-${callParsed.leg}`;
        let putLeg = putPos > 0 ? putParsed.leg : `-${putParsed.leg}`;
        
        // Add open price to call leg if available
        if (callComponent.open_price !== null && callComponent.open_price !== undefined) {
          const callOpenPrice = parseFloat(callComponent.open_price);
          if (!isNaN(callOpenPrice)) {
            callLeg = callPos > 0 ? `${callParsed.leg}@${callOpenPrice}` : `-${callParsed.leg}@${callOpenPrice}`;
          }
        }
        
        // Add open price to put leg if available
        if (putComponent.open_price !== null && putComponent.open_price !== undefined) {
          const putOpenPrice = parseFloat(putComponent.open_price);
          if (!isNaN(putOpenPrice)) {
            putLeg = putPos > 0 ? `${putParsed.leg}@${putOpenPrice}` : `-${putParsed.leg}@${putOpenPrice}`;
          }
        }
        
        // For long synthetic: long call (positive) + short put (negative)
        // For short synthetic: short call (negative) + long put (positive)
        const legs = `${putLeg},${callLeg}`;
        
        return `https://optionstrat.com/build/${strategy}/${ticker}/${legs}`;
      } catch (error) {
        return null;
      }
    }

    // Handle regular option positions
    if (!occSymbol || position.asset_category !== 'OPT') {
      return null;
    }

    try {
      const parsed = parseOccSymbol(occSymbol);
      if (!parsed) {
        return null;
      }

      // Determine strategy based on position
      const isLong = parseFloat(position.position || 0) > 0;
      const strategy = parsed.optionType === 'C' 
        ? (isLong ? 'long-call' : 'short-call')
        : (isLong ? 'long-put' : 'short-put');

      // For short positions, add "-" prefix to the leg
      let legWithPrice = isLong ? parsed.leg : `-${parsed.leg}`;
      
      // Add open price to the leg if available
      if (position.open_price !== null && position.open_price !== undefined) {
        const openPrice = parseFloat(position.open_price);
        if (!isNaN(openPrice)) {
          legWithPrice = isLong 
            ? `${parsed.leg}@${openPrice}` 
            : `-${parsed.leg}@${openPrice}`;
        }
      }

      return `https://optionstrat.com/build/${strategy}/${parsed.ticker}/${legWithPrice}`;
    } catch (error) {
      return null;
    }
  };

  // Bepaal de delta class voor short call posities
  const getDeltaClass = (position) => {
    const putCall = (position.put_call || '').toUpperCase();
    const posValue = parseFloat(position.position || 0);
    const delta = position.delta !== null && position.delta !== undefined ? parseFloat(position.delta) : null;
    
    // Alleen voor short calls (put_call === 'C' en position < 0)
    if (putCall === 'C' && posValue < 0 && delta !== null) {
      const absDelta = Math.abs(delta);
      
      if (absDelta >= 0.15 && absDelta <= 0.25) {
        return 'delta-green';
      } else if (absDelta <= 0.15 || (absDelta > 0.25 && absDelta <= 0.45)) {
        return 'delta-orange';
      } else if (absDelta > 0.45) {
        return 'delta-red';
      }
    }
    
    return '';
  };

  // Bereken moneyness (ITM/OTM/ATM) voor opties inclusief premie
  const calculateMoneyness = (position) => {
    // Alleen voor opties
    if (!position || position.asset_category !== 'OPT' || !position.put_call) {
      return { status: 'N/A', label: 'N/A', color: 'gray' };
    }

    // Check vereiste velden
    const strikeStr = position.strike;
    // Use underlying_price from API (comes from SymbolMapping.current_price)
    const underlyingPrice = position.underlying_price;
    const putCall = (position.put_call || '').toUpperCase();
    const positionValue = parseFloat(position.position || 0);
    const isLong = positionValue > 0;

    // Parse strike
    let strike;
    try {
      strike = parseFloat(strikeStr);
      if (isNaN(strike)) {
        return { status: 'N/A', label: 'N/A', color: 'gray' };
      }
    } catch (e) {
      return { status: 'N/A', label: 'N/A', color: 'gray' };
    }

    // Use in_the_money from API if available (most reliable)
    // Otherwise calculate it ourselves
    let isITM = null;
    let isATM = false;
    let intrinsicValue = 0;
    
    if (position.in_the_money !== null && position.in_the_money !== undefined) {
      // Use API value directly
      isITM = position.in_the_money === true;
    } else {
      // Fallback: calculate from underlying price and strike
      // Check underlying price
      const underlyingPrice = position.underlying_price;
      if (underlyingPrice === null || underlyingPrice === undefined) {
        return { status: 'N/A', label: 'N/A', color: 'gray' };
      }

      const underlying = parseFloat(underlyingPrice);
      if (isNaN(underlying)) {
        return { status: 'N/A', label: 'N/A', color: 'gray' };
      }

      // Bereken intrinsieke waarde
      if (putCall === 'C') {
        // Call: max(0, underlying - strike)
        intrinsicValue = Math.max(0, underlying - strike);
      } else if (putCall === 'P') {
        // Put: max(0, strike - underlying)
        intrinsicValue = Math.max(0, strike - underlying);
      }

      // Check ATM (binnen 0.5% verschil)
      const percentageDiff = Math.abs(underlying - strike) / strike;
      isATM = percentageDiff < 0.005;
      isITM = intrinsicValue > 0;
    }

    // Bepaal premie (open_price of cost_basis_price als fallback)
    let premium = null;
    if (position.open_price !== null && position.open_price !== undefined) {
      premium = parseFloat(position.open_price);
    } else if (position.cost_basis_price !== null && position.cost_basis_price !== undefined) {
      premium = parseFloat(position.cost_basis_price);
    }

    if (premium === null || isNaN(premium)) {
      return { status: 'N/A', label: 'N/A', color: 'gray' };
    }

    // Gebruik mark_price als beschikbaar (huidige marktprijs = intrinsieke + tijdswaarde)
    // Anders gebruik intrinsieke waarde (als we die hebben berekend)
    let currentValue = intrinsicValue;
    if (position.mark_price !== null && position.mark_price !== undefined) {
      const markPrice = parseFloat(position.mark_price);
      if (!isNaN(markPrice)) {
        currentValue = markPrice;
      }
    }

    // Bereken netto waarde (huidige waarde vs premie betaald/ontvangen)
    let netValue;
    if (isLong) {
      // Long: huidige waarde - premie betaald
      netValue = currentValue - premium;
    } else {
      // Short: premie ontvangen - huidige waarde
      netValue = premium - currentValue;
    }

    // Bepaal status (ITM/OTM/ATM) - gebruik in_the_money van API als beschikbaar
    // Als ATM niet gedetecteerd is, check opnieuw voor ATM
    if (!isATM && position.underlying_price && position.strike) {
      const underlying = parseFloat(position.underlying_price);
      const strikeNum = parseFloat(position.strike);
      if (!isNaN(underlying) && !isNaN(strikeNum) && strikeNum > 0) {
        const percentageDiff = Math.abs(underlying - strikeNum) / strikeNum;
        isATM = percentageDiff < 0.005;
      }
    }

    // Bepaal status label (ITM/OTM/ATM)
    let statusLabel;
    if (isATM) {
      statusLabel = 'ATM';
    } else if (isITM === true) {
      statusLabel = 'ITM';
    } else if (isITM === false) {
      statusLabel = 'OTM';
    } else {
      // Fallback: als in_the_money niet beschikbaar is, gebruik berekende intrinsicValue
      statusLabel = intrinsicValue > 0 ? 'ITM' : 'OTM';
    }

    // Bepaal kleur puur op basis van winst/verlies (netValue)
    // Groen = winst, Rood = verlies, Oranje/Grijs = break-even
    let color;
    const breakEvenThreshold = 0.01; // Binnen 1 cent = break-even
    
    if (Math.abs(netValue) < breakEvenThreshold) {
      // Break-even: oranje voor ATM, grijs voor ITM/OTM
      color = isATM ? 'orange' : 'gray';
    } else if (netValue > 0) {
      // Winst
      color = 'green';
    } else {
      // Verlies
      color = 'red';
    }

    return { status: statusLabel, label: statusLabel, color: color };
  };

  // Bepaal het type van een positie op basis van put_call en position
  const getPositionType = (position) => {
    const assetCategory = (position.asset_category || '').toUpperCase();
    const putCall = (position.put_call || '').toUpperCase();
    const posValue = parseFloat(position.position || 0);
    
    // Synthetic posities hebben asset_category "Synthetic" en krijgen eigen sectie
    if (assetCategory === 'SYNTHETIC') {
      return 'synthetic';
    }
    
    // Stocks zijn posities zonder put_call (geen opties)
    if (!putCall || putCall === '' || assetCategory === 'STK') {
      return 'stocks';
    }
    
    if (putCall === 'C' && posValue > 0) return 'long_calls';
    if (putCall === 'C' && posValue < 0) return 'short_calls';
    if (putCall === 'P' && posValue > 0) return 'long_puts';
    if (putCall === 'P' && posValue < 0) return 'short_puts';
    return 'other';
  };

  // Vind synthetic posities (long call + short put metzelfde underlying, strike, expiry)
  const findSyntheticPositions = (positions) => {
    const synthetics = [];
    const usedPositions = new Set();
    const componentsMap = new Map();
    
    // Groepeer opties per underlying, strike, expiry
    // A synthetic is: long call + short put with same underlying_symbol, strike, and expiry
    const optionGroups = {};
    positions.forEach((pos, idx) => {
      const putCall = (pos.put_call || '').toUpperCase().trim();
      // Use underlying_symbol if available, otherwise extract from symbol
      const underlying = (pos.underlying_symbol || pos.symbol || '').trim();
      // Normalize strike and expiry (remove whitespace)
      const strike = (pos.strike || '').toString().trim();
      const expiry = (pos.expiry || '').toString().trim();
      
      // Only process options (C or P) with all required fields
      if ((putCall === 'C' || putCall === 'P') && underlying && strike && expiry) {
        // Create a unique key for grouping: underlying_strike_expiry
        const key = `${underlying}_${strike}_${expiry}`;
        if (!optionGroups[key]) {
          optionGroups[key] = { calls: [], puts: [] };
        }
        if (putCall === 'C') {
          optionGroups[key].calls.push({ ...pos, _index: idx });
        } else if (putCall === 'P') {
          optionGroups[key].puts.push({ ...pos, _index: idx });
        }
      }
    });
    
    // Zoek naar combinaties: long call + short put = synthetic long
    // Match criteria: same underlying_symbol, same strike, same expiry
    Object.keys(optionGroups).forEach(key => {
      const group = optionGroups[key];
      // Find long calls (position > 0)
      const longCalls = group.calls.filter(call => parseFloat(call.position || 0) > 0);
      // Find short puts (position < 0)
      const shortPuts = group.puts.filter(put => parseFloat(put.position || 0) < 0);
      
      // Match each long call with each short put in the same group
      longCalls.forEach(call => {
        shortPuts.forEach(put => {
          // Double-check they match on underlying, strike, and expiry
          const callUnderlying = (call.underlying_symbol || call.symbol || '').trim();
          const putUnderlying = (put.underlying_symbol || put.symbol || '').trim();
          const callStrike = (call.strike || '').toString().trim();
          const putStrike = (put.strike || '').toString().trim();
          const callExpiry = (call.expiry || '').toString().trim();
          const putExpiry = (put.expiry || '').toString().trim();
          
          if (callUnderlying === putUnderlying && 
              callStrike === putStrike && 
              callExpiry === putExpiry) {
            // This is a synthetic long stock position
            const callPos = parseFloat(call.position || 0);
            const putPos = parseFloat(put.position || 0);
            
            // Use a unique identifier - try id first, then conid, then index
            const callId = call.id || call.conid || call._index || Math.random();
            const putId = put.id || put.conid || put._index || Math.random();
            const syntheticId = `synthetic_${callId}_${putId}`;
            
            // Calculate net premium for synthetic position
            // Long calls/puts = costs (money paid, positive)
            // Short calls/puts = money received (negative, should be deducted)
            // For synthetic long: long call (cost) - short put (credit received)
            // For synthetic short: long put (cost) - short call (credit received)
            let syntheticPremium = null;
            const callOpenPrice = call.open_price !== null && call.open_price !== undefined 
              ? parseFloat(call.open_price) 
              : (call.cost_basis_price !== null && call.cost_basis_price !== undefined 
                  ? parseFloat(call.cost_basis_price) 
                  : null);
            const putOpenPrice = put.open_price !== null && put.open_price !== undefined 
              ? parseFloat(put.open_price) 
              : (put.cost_basis_price !== null && put.cost_basis_price !== undefined 
                  ? parseFloat(put.cost_basis_price) 
                  : null);
            
            if (callOpenPrice !== null && putOpenPrice !== null && !isNaN(callOpenPrice) && !isNaN(putOpenPrice)) {
              if (callPos > 0 && putPos < 0) {
                // Synthetic long: long call (cost) - short put (credit received)
                // Short put means we received money, so subtract it from the cost
                syntheticPremium = callOpenPrice - Math.abs(putOpenPrice);
              } else if (callPos < 0 && putPos > 0) {
                // Synthetic short: long put (cost) - short call (credit received)
                // Short call means we received money, so subtract it from the cost
                syntheticPremium = putOpenPrice - Math.abs(callOpenPrice);
              }
            }
            
            const synthetic = {
              ...call,
              id: syntheticId,
              symbol: callUnderlying,
              underlying_symbol: callUnderlying,
              description: `Synthetic ${callUnderlying} (${callStrike} ${callExpiry})`,
              asset_category: 'Synthetic',
              position: callPos, // Synthetic long has positive position (same as long call)
              position_value: parseFloat(call.position_value || 0) + parseFloat(put.position_value || 0),
              fifo_pnl_unrealized: parseFloat(call.fifo_pnl_unrealized || 0) + parseFloat(put.fifo_pnl_unrealized || 0),
              currency: call.currency || put.currency,
              account_id: call.account_id,
              strike: callStrike,
              expiry: callExpiry,
              open_price: syntheticPremium, // Set calculated net premium
              _isSynthetic: true,
              _callId: callId,
              _putId: putId
            };
            
            synthetics.push(synthetic);
            componentsMap.set(syntheticId, [call, put]);
            
            // Track used positions by id, conid, or index
            if (call.id) usedPositions.add(call.id);
            if (put.id) usedPositions.add(put.id);
            if (call.conid) usedPositions.add(`conid_${call.conid}`);
            if (put.conid) usedPositions.add(`conid_${put.conid}`);
            if (call._index !== undefined) usedPositions.add(`index_${call._index}`);
            if (put._index !== undefined) usedPositions.add(`index_${put._index}`);
          }
        });
      });
    });
    
    return { synthetics, usedPositions, componentsMap };
  };

  const toggleSynthetic = (syntheticId) => {
    setExpandedSynthetics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(syntheticId)) {
        newSet.delete(syntheticId);
      } else {
        newSet.add(syntheticId);
      }
      return newSet;
    });
  };

  // Filter en groepeer posities
  const getFilteredAndGroupedPositions = () => {
    let filtered = positions;
    let syntheticComponentsMap = new Map();
    
    // Filter op basis van submenu: options of stocks
    const currentSubmenu = activeSubmenu || 'options'; // Default to 'options'
    if (currentSubmenu === 'options') {
      // Only show options (exclude stocks)
      filtered = filtered.filter(pos => {
        const assetCategory = (pos.asset_category || '').toUpperCase();
        return assetCategory === 'OPT' || assetCategory === 'SYNTHETIC';
      });
    } else if (currentSubmenu === 'stocks') {
      // Only show stocks
      filtered = filtered.filter(pos => {
        const assetCategory = (pos.asset_category || '').toUpperCase();
        return assetCategory === 'STK';
      });
    }
    // For other submenus (short-put-exposure, rolled-options), don't filter by asset type
    
    // Als synthetic checkbox aan staat, combineer long calls + short puts
    if (showSynthetic && (currentSubmenu === 'options' || !currentSubmenu)) {
      const { synthetics, usedPositions, componentsMap } = findSyntheticPositions(filtered);
      syntheticComponentsMap = componentsMap;
      // Verwijder de gebruikte call en put posities
      filtered = filtered.filter(pos => {
        // Check multiple identifiers to ensure we filter correctly
        const posId = pos.id;
        const posConid = pos.conid;
        const posIndex = pos._index;
        return !(
          (posId && usedPositions.has(posId)) ||
          (posConid && usedPositions.has(`conid_${posConid}`)) ||
          (posIndex !== undefined && usedPositions.has(`index_${posIndex}`))
        );
      });
      // Voeg synthetic posities toe
      filtered = [...filtered, ...synthetics];
    }
    
    // Filter op type als er een selectie is (alleen voor options view)
    if (selectedType !== 'all' && currentSubmenu === 'options') {
      filtered = filtered.filter(pos => getPositionType(pos) === selectedType);
    }
    
    // Groepeer en sorteer: eerst op type, dan op symbol
    const grouped = filtered.reduce((acc, pos) => {
      const type = getPositionType(pos);
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(pos);
      return acc;
    }, {});
    
    // Sorteer binnen elke groep op symbol
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const symbolA = (a.underlying_symbol || a.symbol || '').toLowerCase();
        const symbolB = (b.underlying_symbol || b.symbol || '').toLowerCase();
        return symbolA.localeCompare(symbolB);
      });
    });
    
    // Flatten in de juiste volgorde based on submenu
    // currentSubmenu is already declared at the top of this function
    let order;
    if (currentSubmenu === 'stocks') {
      order = ['stocks'];
    } else {
      // For options: exclude stocks
      order = ['synthetic', 'long_calls', 'short_calls', 'long_puts', 'short_puts', 'other'];
    }
    
    const result = [];
    order.forEach(type => {
      if (grouped[type]) {
        result.push(...grouped[type]);
      }
    });
    
    return { positions: result, componentsMap: syntheticComponentsMap };
  };

  const { positions: filteredPositions, componentsMap: syntheticComponents } = getFilteredAndGroupedPositions();

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPositions = (positions) => {
    if (!sortConfig.key) return positions;
    
    return [...positions].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'symbol') {
        aVal = a.underlying_symbol || a.symbol || '';
        bVal = b.underlying_symbol || b.symbol || '';
      }
      
      // Handle date comparison - parse DD/MM/YY format
      if (sortConfig.key === 'expiry') {
        const parseDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return 0;
          // Parse DD/MM/YY format
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);
            // Handle 2-digit years: assume 20xx for years < 50, 19xx for years >= 50
            const fullYear = year < 50 ? 2000 + year : 1900 + year;
            return new Date(fullYear, month, day).getTime();
          }
          return 0;
        };
        aVal = parseDate(a.expiry);
        bVal = parseDate(b.expiry);
      }
      // Handle numeric values
      else if (['position', 'position_value', 'fifo_pnl_unrealized', 'days_until_expiry', 'mark_price'].includes(sortConfig.key)) {
        // For days_until_expiry, handle null/undefined as very large number (expired options go to end)
        if (sortConfig.key === 'days_until_expiry') {
          aVal = aVal !== null && aVal !== undefined ? parseFloat(aVal) : 999999;
          bVal = bVal !== null && bVal !== undefined ? parseFloat(bVal) : 999999;
        } else {
          aVal = parseFloat(aVal || 0);
          bVal = parseFloat(bVal || 0);
        }
      }
      // Handle string comparison
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Calculate exposure for short options (calls or puts)
  const calculateShortOptionExposure = (position) => {
    const strike = parseFloat(position.strike || 0);
    const positionValue = parseFloat(position.position || 0); // negative for short
    const multiplier = parseFloat(position.multiplier || 100); // default to 100 for standard options
    const contracts = Math.abs(positionValue); // number of contracts (absolute value)
    
    // Exposure = strike price * multiplier * number of contracts
    // For short calls: amount needed to buy stock if assigned
    // For short puts: amount needed to buy stock if assigned (same calculation)
    return strike * multiplier * contracts;
  };

  // Calculate days until expiry
  const calculateDaysUntilExpiry = (expiryStr) => {
    if (!expiryStr) return null;
    try {
      // Parse expiry date format: DD/MM/YY
      const parts = expiryStr.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = 2000 + parseInt(parts[2], 10);
        const expiryDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expiryDate.setHours(0, 0, 0, 0);
        const diffTime = expiryDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 ? diffDays : null; // Return null if expired
      }
    } catch (e) {
      console.error('Error parsing expiry date:', expiryStr, e);
    }
    return null;
  };

  // Short Put Exposure Table Component
  const ShortPutExposureTable = ({ positions, formatCurrency }) => {
    const [exposureSortConfig, setExposureSortConfig] = useState({ key: 'days_until_expiry', direction: 'asc' });

    // Filter to only short puts
    const shortPuts = positions.filter(pos => {
      const putCall = (pos.put_call || '').toUpperCase();
      const posValue = parseFloat(pos.position || 0);
      return putCall === 'P' && posValue < 0;
    });

    // Calculate exposure and days until expiry for each short put
    const shortPutsWithExposure = shortPuts.map(pos => ({
      ...pos,
      exposure: calculateShortOptionExposure(pos),
      days_until_expiry: calculateDaysUntilExpiry(pos.expiry)
    }));

    // Handle sorting
    const handleExposureSort = (key) => {
      setExposureSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    };

    const getExposureSortIcon = (key) => {
      if (exposureSortConfig.key !== key) return '↕';
      return exposureSortConfig.direction === 'asc' ? '↑' : '↓';
    };

    // Sort the data
    shortPutsWithExposure.sort((a, b) => {
      const { key, direction } = exposureSortConfig;
      let aVal, bVal;

      if (key === 'days_until_expiry') {
        aVal = a.days_until_expiry !== null && a.days_until_expiry !== undefined ? a.days_until_expiry : 999999;
        bVal = b.days_until_expiry !== null && b.days_until_expiry !== undefined ? b.days_until_expiry : 999999;
      } else if (key === 'symbol') {
        aVal = (a.underlying_symbol || a.symbol || '').toLowerCase();
        bVal = (b.underlying_symbol || b.symbol || '').toLowerCase();
      } else if (key === 'strike') {
        aVal = parseFloat(a.strike || 0);
        bVal = parseFloat(b.strike || 0);
      } else if (key === 'position') {
        aVal = parseFloat(a.position || 0);
        bVal = parseFloat(b.position || 0);
      } else if (key === 'exposure') {
        aVal = a.exposure || 0;
        bVal = b.exposure || 0;
      } else {
        return 0;
      }

      if (typeof aVal === 'string') {
        return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    // Calculate total exposure
    const totalExposure = shortPutsWithExposure.reduce((sum, pos) => sum + pos.exposure, 0);

    if (shortPutsWithExposure.length === 0) {
      return (
        <div className="empty-state">
          <p>No short put positions found.</p>
        </div>
      );
    }

    return (
      <div className="short-call-exposure-container">
        <table className="short-call-exposure-table">
          <thead>
            <tr>
              <th>Account</th>
              <th className="sortable-header" onClick={() => handleExposureSort('symbol')}>
                Symbol <span className="sort-icon">{getExposureSortIcon('symbol')}</span>
              </th>
              <th className="sortable-header" onClick={() => handleExposureSort('strike')}>
                Strike <span className="sort-icon">{getExposureSortIcon('strike')}</span>
              </th>
              <th>Expiry</th>
              <th className="sortable-header" onClick={() => handleExposureSort('days_until_expiry')}>
                Days Until Expiry <span className="sort-icon">{getExposureSortIcon('days_until_expiry')}</span>
              </th>
              <th className="sortable-header" onClick={() => handleExposureSort('position')}>
                Position <span className="sort-icon">{getExposureSortIcon('position')}</span>
              </th>
              <th className="sortable-header" onClick={() => handleExposureSort('exposure')}>
                Exposure <span className="sort-icon">{getExposureSortIcon('exposure')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shortPutsWithExposure.map((position, idx) => (
              <tr key={position.id || position.conid || idx}>
                <td className="account-cell">{getAccountDisplayName(position.account_id)}</td>
                <td className="symbol-cell">{position.underlying_symbol || position.symbol || '-'}</td>
                <td className="number-cell">{formatNumber(position.strike || '-', 2)}</td>
                <td className="date-cell">{position.expiry || '-'}</td>
                <td className="number-cell days-until-expiry-cell">
                  {position.days_until_expiry !== null && position.days_until_expiry !== undefined 
                    ? position.days_until_expiry 
                    : '-'}
                </td>
                <td className="number-cell">{formatInteger(position.position)}</td>
                <td className="number-cell exposure-cell">{formatCurrency(position.exposure, position.currency || 'EUR')}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td colSpan="6" className="total-label"><strong>Total Exposure</strong></td>
              <td className="number-cell exposure-cell total-exposure">
                <strong>{formatCurrency(totalExposure, 'EUR')}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="open-positions-container">
      <div className="open-positions-content">
        <div className="open-positions-controls">
          {accounts.length > 0 && (
            <div className="broker-selector">
              <label htmlFor="broker-select">Broker: </label>
              <select
                id="broker-select"
                value={selectedBroker}
                onChange={(e) => { setSelectedBroker(e.target.value); setSelectedAccount('all'); }}
              >
                <option value="all">All brokers</option>
                <option value="ibkr">IBKR</option>
                <option value="tastytrade">TastyTrade</option>
              </select>
            </div>
          )}
          {accounts.length > 0 && (
            <div className="account-selector">
              <label htmlFor="account-select">Account: </label>
              <select
                id="account-select"
                value={selectedAccount || 'all'}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <option value="all">All accounts</option>
                {accounts
                  .filter(acc => selectedBroker === 'all' || acc.broker === selectedBroker)
                  .map(account => (
                    <option key={account.id} value={account.id}>
                      {account.display_name || account.broker_account_id}
                    </option>
                  ))}
              </select>
            </div>
          )}
          {(activeSubmenu === 'options' || !activeSubmenu || activeSubmenu === 'open-positions') && (
            <>
              <div className="type-selector">
                <label htmlFor="type-select">Type: </label>
                <select
                  id="type-select"
                  value={selectedType || 'all'}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="all">All types</option>
                  <option value="long_calls">Long Calls</option>
                  <option value="short_calls">Short Calls</option>
                  <option value="long_puts">Long Puts</option>
                  <option value="short_puts">Short Puts</option>
                </select>
              </div>
              <div className="synthetic-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={showSynthetic}
                    onChange={(e) => setShowSynthetic(e.target.checked)}
                  />
                  <span>Show Synthetics</span>
                </label>
              </div>
            </>
          )}
        </div>

        {loading && <div className="loading">Loading...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            {activeSubmenu === 'short-put-exposure' ? (
              <ShortPutExposureTable positions={positions} formatCurrency={formatCurrency} />
            ) : activeSubmenu === 'rolled-options' ? (
              <RolledOptions />
            ) : filteredPositions.length === 0 ? (
              <div className="empty-state">
                <p>No open positions found for the selected filters.</p>
              </div>
            ) : (
              <div className="short-call-exposure-container">
                {(() => {
                  // Group positions by type
                  const grouped = filteredPositions.reduce((acc, pos, idx) => {
                    const type = getPositionType(pos);
                    if (!acc[type]) {
                      acc[type] = [];
                    }
                    acc[type].push({ ...pos, _index: idx });
                    return acc;
                  }, {});
                  
                  // Determine order based on submenu
                  const currentSubmenu = activeSubmenu || 'options';
                  const stocksOrder = ['stocks'];
                  const optionsOrder = ['short_calls', 'long_calls', 'short_puts', 'long_puts', 'synthetic', 'other'];
                  
                  // Apply sorting to grouped positions
                  const sortedGrouped = {};
                  Object.keys(grouped).forEach(type => {
                    if (grouped[type] && grouped[type].length > 0) {
                      sortedGrouped[type] = getSortedPositions(grouped[type]);
                    }
                  });
                  
                  const PositionRowWithTags = ({ position, showExpiry, isStocksType }) => {
                    const isSynthetic = position.asset_category === 'Synthetic';
                    // Live positions (position.is_live - see LiveOpenPosition/
                    // OpenPositionViewSet) have a synthetic negative id, not a
                    // real OpenPosition row - tagging would PATCH a row that
                    // doesn't exist. Tags become available again automatically
                    // once tonight's Flex import turns this into a real row.
                    const canHaveTags = !isSynthetic && !position.is_live && (position.asset_category === 'STK' || position.asset_category === 'OPT');
                    const [tagIds, setTagIds] = useState(position.tags ? position.tags.map(t => t.id) : []);
                    const [saving, setSaving] = useState(false);
                    const syntheticId = isSynthetic ? position.id : null;
                    const isExpanded = syntheticId && expandedSynthetics.has(syntheticId);
                    const components = syntheticId ? syntheticComponents.get(syntheticId) : null;

                    const handleTagChange = async (newTagIds) => {
                      if (!canHaveTags) return;
                      setTagIds(newTagIds);
                      try {
                        setSaving(true);
                        await axios.patch(`${API_URL}/portfolio/open-positions/${position.id}/`, {
                          tag_ids: newTagIds,
                        });
                        // Update the position in the local state
                        const updatedPosition = { ...position, tags: position.tags ? position.tags.filter(t => newTagIds.includes(t.id)) : [] };
                        setPositions(prev => prev.map(p => p.id === position.id ? updatedPosition : p));
                      } catch (err) {
                        console.error('Error updating tags:', err);
                        setTagIds(position.tags ? position.tags.map(t => t.id) : []);
                      } finally {
                        setSaving(false);
                      }
                    };

                    return (
                      <React.Fragment key={position.id || position._index}>
                        <tr className={(isStocksType ? 'stocks-row' :
                          position.asset_category === 'Synthetic' ? 'synthetic-row' :
                          (position.put_call || '').toUpperCase() === 'C' ? 'row-call' : 'row-put') +
                          (position.is_live ? ' live-position-row' : '')}>
                          <td className="account-cell">
                            {isSynthetic && (
                              <button 
                                className="expand-button"
                                onClick={() => toggleSynthetic(syntheticId)}
                                title={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? '−' : '+'}
                              </button>
                            )}
                            {getAccountDisplayName(position.account_id)}
                          </td>
                          <td className="symbol-cell">
                            {position.underlying_symbol || position.symbol || '-'}
                            {position.is_live && (
                              <span className="live-position-badge" title="Seen via the IBKR API today, not yet in tonight's Flex import">LIVE</span>
                            )}
                          </td>
                          {isStocksType && (
                            <td className="number-cell">{formatInteger(position.position)}</td>
                          )}
                          {isStocksType && (
                            <td className="number-cell">
                              {position.open_price !== null && position.open_price !== undefined
                                ? formatCurrency(position.open_price, position.currency || 'USD')
                                : '-'}
                            </td>
                          )}
                          {!isStocksType && (
                            <td className="optionstrat-cell">
                              {(() => {
                                // Check if we have optionstrat_url in database first
                                const dbUrl = position.optionstrat_url;
                                // Calculate URL if not in database
                                // Pass synthetic components if this is a synthetic position
                                const calculatedUrl = !dbUrl ? calculateOptionStratUrl(position.symbol, position, components) : null;
                                const optionStratUrl = dbUrl || calculatedUrl;
                                
                                if (optionStratUrl && optionStratUrl.startsWith('https://')) {
                                  return (
                                    <a 
                                      href={optionStratUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="optionstrat-link"
                                    >
                                      Link
                                    </a>
                                  );
                                } else {
                                  // Determine reason for NVT
                                  let reason = 'OptionStrat URL cannot be generated';
                                  if (position.asset_category !== 'OPT') {
                                    reason = 'Not an option position';
                                  } else if (!position.symbol) {
                                    reason = 'Option symbol is missing';
                                  } else {
                                    reason = 'Option symbol format is not supported by OptionStrat';
                                  }
                                  
                                  return (
                                    <span className="optionstrat-nvt">
                                      NVT
                                      <span className="info-icon" title={reason}>ℹ️</span>
                                    </span>
                                  );
                                }
                              })()}
                            </td>
                          )}
                          {!isStocksType && (
                            <td className="number-cell">
                              {position.open_price !== null && position.open_price !== undefined
                                ? formatCurrency(position.open_price, position.currency || 'USD')
                                : '-'}
                            </td>
                          )}
                          {!isStocksType && (
                            <td className="number-cell">
                              {position.strike !== null && position.strike !== undefined
                                ? formatCurrency(position.strike, position.currency || 'USD')
                                : '-'}
                            </td>
                          )}
                          {isStocksType && (
                            <td
                              className="number-cell"
                              onMouseEnter={() => handleCellMouseEnter(position, 'mark_price')}
                              onMouseLeave={handleCellMouseLeave}
                              style={{ position: 'relative' }}
                            >
                              {position.mark_price !== null && position.mark_price !== undefined
                                ? formatCurrency(position.mark_price, position.currency || 'USD')
                                : '-'}
                              {hoveredCell?.positionId === position.id && hoveredCell?.field === 'mark_price' && (
                                <div className="cell-tooltip">
                                  {formatLastUpdated(position.mark_price_updated_at)}
                                </div>
                              )}
                            </td>
                          )}
                          {isStocksType && (
                            <td 
                              className={(() => {
                                const vspInfo = vspData[position.id];
                                const markPrice = parseFloat(position.mark_price) || 0;
                                if (vspInfo && vspInfo.vsp !== undefined && vspInfo.vsp !== null && markPrice > 0) {
                                  // Green if VSP < current price (good deal), Orange if VSP > current price
                                  const vsp = parseFloat(vspInfo.vsp);
                                  return `number-cell ${vsp < markPrice ? 'positive' : 'vsp-orange'}`;
                                }
                                return 'number-cell';
                              })()}
                              title="Virtual Stock Price"
                            >
                              {(() => {
                                const vspInfo = vspData[position.id];
                                if (vspInfo && vspInfo.vsp !== undefined && vspInfo.vsp !== null) {
                                  return formatCurrency(vspInfo.vsp, position.currency || 'USD');
                                }
                                return '';
                              })()}
                            </td>
                          )}
                          {isStocksType && (
                            <td className={`number-cell ${(() => {
                              const vspInfo = vspData[position.id];
                              return vspInfo && vspInfo.totalPnL >= 0 ? 'positive' : vspInfo && vspInfo.totalPnL < 0 ? 'negative' : '';
                            })()}`}>
                              {(() => {
                                const vspInfo = vspData[position.id];
                                return vspInfo && vspInfo.totalPnL !== undefined && vspInfo.totalPnL !== null
                                  ? formatCurrency(vspInfo.totalPnL, position.currency)
                                  : '';
                              })()}
                            </td>
                          )}
                          {showExpiry && (
                            <td className="expiry-cell">
                              {position.expiry ? position.expiry : '-'}
                            </td>
                          )}
                          {showExpiry && (
                            <td className="number-cell days-until-expiry-cell">
                              {position.days_until_expiry !== null && position.days_until_expiry !== undefined 
                                ? position.days_until_expiry 
                                : '-'}
                            </td>
                          )}
                          {showExpiry && (
                            <td 
                              className="number-cell"
                              onMouseEnter={() => handleCellMouseEnter(position, 'iv')}
                              onMouseLeave={handleCellMouseLeave}
                              style={{ position: 'relative' }}
                            >
                              {position.iv !== null && position.iv !== undefined 
                                ? `${Math.round(parseFloat(position.iv) * 100)}%` 
                                : '-'}
                              {hoveredCell?.positionId === position.id && hoveredCell?.field === 'iv' && (
                                <div className="cell-tooltip">
                                  {formatLastUpdated(position.iv_updated_at)}
                                </div>
                              )}
                            </td>
                          )}
                          {showExpiry && (
                            <td 
                              className={`number-cell ${getDeltaClass(position)}`}
                              onMouseEnter={() => handleCellMouseEnter(position, 'delta')}
                              onMouseLeave={handleCellMouseLeave}
                              style={{ position: 'relative' }}
                            >
                              {position.delta !== null && position.delta !== undefined 
                                ? parseFloat(position.delta).toFixed(2) 
                                : '-'}
                              {isDeltaValueStale(position) && (
                                <span 
                                  className="delta-warning-icon" 
                                  title="De waarde is niet aangepast omdat Massive geen waarde meegaf voor deze optie"
                                >
                                  !
                                </span>
                              )}
                              {hoveredCell?.positionId === position.id && hoveredCell?.field === 'delta' && (
                                <div className="cell-tooltip">
                                  {formatLastUpdated(position.delta_updated_at)}
                                </div>
                              )}
                            </td>
                          )}
                          {showExpiry && (
                            <td className="moneyness-cell">
                              {(() => {
                                const moneyness = calculateMoneyness(position);
                                return (
                                  <span className={`moneyness-badge moneyness-${moneyness.color}`}>
                                    {moneyness.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          {!isStocksType && (
                            <td className="number-cell rolled-pnl-cell">
                              {(() => {
                                const underlying = position.underlying_symbol || (position.symbol ? position.symbol.split()[0] : null);
                                // Use account_id which is the IBKR account ID from OpenPositionSerializer
                                const accountId = position.account_id;
                                if (underlying && accountId) {
                                  const key = `${underlying}_${accountId}`;
                                  const rolledPnL = rolledOptionsData[key];
                                  if (rolledPnL !== undefined && rolledPnL !== null) {
                                    return (
                                      <span className={rolledPnL >= 0 ? 'positive' : 'negative'}>
                                        {formatCurrency(rolledPnL, position.currency)}
                                      </span>
                                    );
                                  }
                                }
                                return '-';
                              })()}
                            </td>
                          )}
                          {!isStocksType && (
                            <td className="number-cell">{formatInteger(position.position)}</td>
                          )}
                          <td className="number-cell">{formatCurrency(position.position_value, position.currency)}</td>
                          <td className={`number-cell ${position.fifo_pnl_unrealized && parseFloat(position.fifo_pnl_unrealized) >= 0 ? 'positive' : 'negative'}`}>
                            {position.fifo_pnl_unrealized ? formatCurrency(position.fifo_pnl_unrealized, position.currency) : '-'}
                          </td>
                          <td className="tags-cell">
                            {canHaveTags ? (
                              <TagSelector
                                selectedTagIds={tagIds}
                                onChange={handleTagChange}
                                disabled={saving}
                                initialTags={position.tags || []}
                              />
                            ) : (
                              <span className="no-tags">-</span>
                            )}
                          </td>
                        </tr>
                        {isSynthetic && isExpanded && components && components.map((component, compIdx) => (
                          <tr key={`${syntheticId}_component_${compIdx}`} className="synthetic-component-row">
                            <td className="account-cell">
                              {/* Empty cell but maintain structure for alignment */}
                              <span style={{ display: 'inline-block', width: '28px' }}></span>
                            </td>
                            <td className="symbol-cell">{component.symbol || '-'}</td>
                            <td className="optionstrat-cell">
                              {(() => {
                                const dbUrl = component.optionstrat_url;
                                // For component rows, don't pass components (they are individual options)
                                const calculatedUrl = !dbUrl ? calculateOptionStratUrl(component.symbol, component, null) : null;
                                const optionStratUrl = dbUrl || calculatedUrl;
                                
                                if (optionStratUrl && optionStratUrl.startsWith('https://')) {
                                  return (
                                    <a 
                                      href={optionStratUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="optionstrat-link"
                                    >
                                      Link
                                    </a>
                                  );
                                } else {
                                  let reason = 'OptionStrat URL cannot be generated';
                                  if (component.asset_category !== 'OPT') {
                                    reason = 'Not an option position';
                                  } else if (!component.symbol) {
                                    reason = 'Option symbol is missing';
                                  } else {
                                    reason = 'Option symbol format is not supported by OptionStrat';
                                  }
                                  
                                  return (
                                    <span className="optionstrat-nvt">
                                      NVT
                                      <span className="info-icon" title={reason}>ℹ️</span>
                                    </span>
                                  );
                                }
                              })()}
                            </td>
                            <td className="number-cell">
                              {component.open_price !== null && component.open_price !== undefined
                                ? formatCurrency(component.open_price, component.currency || 'USD')
                                : '-'}
                            </td>
                            <td className="number-cell">
                              {component.strike !== null && component.strike !== undefined
                                ? formatCurrency(component.strike, component.currency || 'USD')
                                : '-'}
                            </td>
                            {showExpiry && (
                              <td className="expiry-cell">
                                {component.expiry ? component.expiry : '-'}
                              </td>
                            )}
                            {showExpiry && (
                              <td className="number-cell days-until-expiry-cell">
                                {component.days_until_expiry !== null && component.days_until_expiry !== undefined 
                                  ? component.days_until_expiry 
                                  : '-'}
                              </td>
                            )}
                            {showExpiry && (
                              <td 
                                className="number-cell"
                                onMouseEnter={() => handleCellMouseEnter(component, 'iv')}
                                onMouseLeave={handleCellMouseLeave}
                                style={{ position: 'relative' }}
                              >
                                {component.iv !== null && component.iv !== undefined 
                                  ? `${Math.round(parseFloat(component.iv) * 100)}%` 
                                  : '-'}
                                {hoveredCell?.positionId === component.id && hoveredCell?.field === 'iv' && (
                                  <div className="cell-tooltip">
                                    {formatLastUpdated(component.iv_updated_at)}
                                  </div>
                                )}
                              </td>
                            )}
                            {showExpiry && (
                              <td 
                                className={`number-cell ${getDeltaClass(component)}`}
                                onMouseEnter={() => handleCellMouseEnter(component, 'delta')}
                                onMouseLeave={handleCellMouseLeave}
                                style={{ position: 'relative' }}
                              >
                                {component.delta !== null && component.delta !== undefined 
                                  ? parseFloat(component.delta).toFixed(2) 
                                  : '-'}
                                {isDeltaValueStale(component) && (
                                  <span 
                                    className="delta-warning-icon" 
                                    title="De waarde is niet aangepast omdat Massive geen waarde meegaf voor deze optie"
                                  >
                                    !
                                  </span>
                                )}
                                {hoveredCell?.positionId === component.id && hoveredCell?.field === 'delta' && (
                                  <div className="cell-tooltip">
                                    {formatLastUpdated(component.delta_updated_at)}
                                  </div>
                                )}
                              </td>
                            )}
                            <td className="moneyness-cell">-</td>
                            <td className="number-cell rolled-pnl-cell">
                              {(() => {
                                const underlying = component.underlying_symbol || (component.symbol ? component.symbol.split()[0] : null);
                                // Use account_id which is the IBKR account ID from OpenPositionSerializer
                                const accountId = component.account_id;
                                if (underlying && accountId) {
                                  const key = `${underlying}_${accountId}`;
                                  const rolledPnL = rolledOptionsData[key];
                                  if (rolledPnL !== undefined && rolledPnL !== null) {
                                    return (
                                      <span className={rolledPnL >= 0 ? 'positive' : 'negative'}>
                                        {formatCurrency(rolledPnL, component.currency)}
                                      </span>
                                    );
                                  }
                                }
                                return '-';
                              })()}
                            </td>
                            <td className="number-cell">{formatInteger(component.position)}</td>
                            <td className="number-cell">{formatCurrency(component.position_value, component.currency)}</td>
                            <td className={`number-cell ${component.fifo_pnl_unrealized && parseFloat(component.fifo_pnl_unrealized) >= 0 ? 'positive' : 'negative'}`}>
                              {component.fifo_pnl_unrealized ? formatCurrency(component.fifo_pnl_unrealized, component.currency) : '-'}
                            </td>
                            <td className="tags-cell">-</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  };

                  const renderPositionRow = (position, showExpiry, isStocksType) => {
                    return <PositionRowWithTags key={position.id || position.conid || position._index} position={position} showExpiry={showExpiry} isStocksType={isStocksType} />;
                  };
                  
                  // Determine which columns to show based on submenu
                  const isStocksView = currentSubmenu === 'stocks';
                  const isOptionsView = currentSubmenu === 'options' || !currentSubmenu || currentSubmenu === 'open-positions';
                  
                  // Build unified table structure
                  const renderUnifiedTable = () => {
                  // Determine column count (options view now has 16 columns with Open Price)
                  // Stocks view: Account, Symbol, Position, Open Price, Current Price, VSP, P&L VSP, Value, P&L Unrealized, Tags = 10
                  const colCount = isStocksView ? 10 : 16;
                    
                    return (
                      <div className="op-desktop">
                      <table className="open-positions-table">
                        <thead>
                          <tr>
                            {isStocksView ? (
                              <>
                                <th className="sortable-header" onClick={() => handleSort('account_id')}>
                                  Account <span className="sort-icon">{getSortIcon('account_id')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('symbol')}>
                                  Symbol <span className="sort-icon">{getSortIcon('symbol')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('position')}>
                                  Position <span className="sort-icon">{getSortIcon('position')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('open_price')}>
                                  Open Price <span className="sort-icon">{getSortIcon('open_price')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('mark_price')}>
                                  Current Price <span className="sort-icon">{getSortIcon('mark_price')}</span>
                                </th>
                                <th title="Virtual Stock Price">VSP</th>
                                <th>P&L VSP</th>
                                <th className="sortable-header" onClick={() => handleSort('position_value')}>
                                  Value <span className="sort-icon">{getSortIcon('position_value')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('fifo_pnl_unrealized')}>
                                  P&L Unrealized <span className="sort-icon">{getSortIcon('fifo_pnl_unrealized')}</span>
                                </th>
                                <th>Tags</th>
                              </>
                            ) : (
                              <>
                                <th className="sortable-header" onClick={() => handleSort('account_id')}>
                                  Account <span className="sort-icon">{getSortIcon('account_id')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('symbol')}>
                                  Symbol <span className="sort-icon">{getSortIcon('symbol')}</span>
                                </th>
                                <th>OptionStrat</th>
                                <th className="sortable-header" onClick={() => handleSort('open_price')}>
                                  Premium <span className="sort-icon">{getSortIcon('open_price')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('strike')}>
                                  Strike <span className="sort-icon">{getSortIcon('strike')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('expiry')}>
                                  Expiry <span className="sort-icon">{getSortIcon('expiry')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('days_until_expiry')}>
                                  DTE <span className="sort-icon">{getSortIcon('days_until_expiry')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('iv')}>
                                  IV <span className="sort-icon">{getSortIcon('iv')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('delta')}>
                                  Delta <span className="sort-icon">{getSortIcon('delta')}</span>
                                </th>
                                <th>Moneyness</th>
                                <th>Rolled</th>
                                <th className="sortable-header" onClick={() => handleSort('position')}>
                                  Position <span className="sort-icon">{getSortIcon('position')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('position_value')}>
                                  Value <span className="sort-icon">{getSortIcon('position_value')}</span>
                                </th>
                                <th className="sortable-header" onClick={() => handleSort('fifo_pnl_unrealized')}>
                                  P&L Unrealized <span className="sort-icon">{getSortIcon('fifo_pnl_unrealized')}</span>
                                </th>
                                <th>Tags</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {isStocksView ? (
                            <>
                              {sortedGrouped['stocks'] && sortedGrouped['stocks'].length > 0 && (
                                <>
                                  <tr className="group-header-row other-header">
                                    <td colSpan={colCount} className="group-header-cell">STOCKS</td>
                                  </tr>
                                  {sortedGrouped['stocks'].map((position) => renderPositionRow(position, false, true))}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {optionsOrder.map((type) => {
                                if (!sortedGrouped[type] || sortedGrouped[type].length === 0) return null;
                                
                                const positions = sortedGrouped[type];
                                const typeLabel = 
                                  type === 'synthetic' ? 'SYNTHETIC POSITIONS' :
                                  type === 'long_calls' ? 'LONG CALLS' :
                                  type === 'short_calls' ? 'SHORT CALLS' :
                                  type === 'long_puts' ? 'LONG PUTS' :
                                  type === 'short_puts' ? 'SHORT PUTS' :
                                  'OTHER';
                                
                                return (
                                  <React.Fragment key={type}>
                                    <tr className={`group-header-row ${type}-header`}>
                                      <td colSpan={colCount} className="group-header-cell">{typeLabel}</td>
                                    </tr>
                                    {positions.map((position) => renderPositionRow(position, true, false))}
                                  </React.Fragment>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                      </table>
                      </div>
                    );
                  };

                  const renderMobileTable = () => {
                    return (
                      <div className="op-mobile">
                        {isStocksView ? (
                          <table className="op-mobile-table">
                            <thead>
                              <tr>
                                <th>Symbol</th>
                                <th>Qty</th>
                                <th>Value</th>
                                <th>P&L</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedGrouped['stocks'] && sortedGrouped['stocks'].length > 0 && (
                                <>
                                  <tr className="section-header">
                                    <td colSpan={4}>STOCKS</td>
                                  </tr>
                                  {sortedGrouped['stocks'].map((position) => (
                                    <tr key={position.id || position._index}>
                                      <td>{position.underlying_symbol || position.symbol || '-'}</td>
                                      <td>{formatInteger(position.position)}</td>
                                      <td>{formatCurrency(position.position_value, position.currency)}</td>
                                      <td className={position.fifo_pnl_unrealized && parseFloat(position.fifo_pnl_unrealized) >= 0 ? 'positive' : 'negative'}>
                                        {position.fifo_pnl_unrealized ? formatCurrency(position.fifo_pnl_unrealized, position.currency) : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <table className="op-mobile-table">
                            <thead>
                              <tr>
                                <th>Symbol</th>
                                <th>Prem.</th>
                                <th>Strike</th>
                                <th>Expiry</th>
                                <th>P&L</th>
                              </tr>
                            </thead>
                            <tbody>
                              {optionsOrder.map((type) => {
                                if (!sortedGrouped[type] || sortedGrouped[type].length === 0) return null;
                                const typePositions = sortedGrouped[type];
                                const typeLabel =
                                  type === 'synthetic' ? 'SYNTHETIC' :
                                  type === 'long_calls' ? 'LONG CALLS' :
                                  type === 'short_calls' ? 'SHORT CALLS' :
                                  type === 'long_puts' ? 'LONG PUTS' :
                                  type === 'short_puts' ? 'SHORT PUTS' :
                                  'OTHER';
                                return (
                                  <React.Fragment key={type}>
                                    <tr className="section-header">
                                      <td colSpan={5}>{typeLabel}</td>
                                    </tr>
                                    {typePositions.map((position) => {
                                      const isCall = position.put_call === 'C';
                                      const pnl = position.fifo_pnl_unrealized ? parseFloat(position.fifo_pnl_unrealized) : null;
                                      return (
                                        <tr key={position.id || position._index} className={isCall ? 'row-call' : 'row-put'}>
                                          <td>{position.underlying_symbol || position.symbol || '-'}</td>
                                          <td>{position.open_price != null ? formatCurrency(position.open_price, position.currency || 'USD') : '-'}</td>
                                          <td>{position.strike != null ? formatCurrency(position.strike, position.currency || 'USD') : '-'}</td>
                                          <td>{position.expiry || '-'}</td>
                                          <td className={pnl !== null ? (pnl >= 0 ? 'positive' : 'negative') : ''}>
                                            {pnl !== null ? formatCurrency(pnl, position.currency) : '-'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {renderUnifiedTable()}
                      {renderMobileTable()}
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OpenPositions;







